import Button from '@atlaskit/button/standard-button';
import {
  CopyableText,
  EntitlementIcon,
  isAccess,
  isResourceAccess,
  OpenSidebarPayload,
  SummarySectionV2,
  useFeatureToggle,
  useModalDialog,
  useSidebar,
} from '@css-xp/base';
import { TransactionAccountContext } from '@css-xp/base/common-context';
import {
  BillingFrequency,
  BillingStatus,
  EntitlementDetails,
  InvoiceGroup,
  LinkedDestinationEntitlement,
} from '@css-xp/graphtypes/src/generated-types-new';
import { Permissions } from '@atlassian/cssxp-typescommon';
import { getEntitlementRouteCCPV2, getUserTier, isDeletedEntitlement } from '@atlassian/cssxp-utils-entitlement';
import { ENABLE_SANDBOX_ENTITLEMENT } from '@atlassian/cssxp-utils-generic';
import { isBilledElsewhereProduct, isJCAProduct } from '@atlassian/cssxp-utils-product';
import { defaultTo, lowerCase } from 'lodash';
import React from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { SIDEBAR } from '../../../../entitlement-page/src/components/entitlement-page';
import { validateSubscriptionDetails } from '../../../../entitlement-page/src/components/entitlement-page-utils';

interface props {
  entitlement: EntitlementDetails;
  linkedEntitlements: LinkedDestinationEntitlement[];
  isHistoryButtonEnabled?: boolean;
  isProductRedirectEnabled?: boolean;
}

export const UserTier = ({ entitlement }: { entitlement: EntitlementDetails }) => {
  const [isEnabled] = useFeatureToggle();
  const isAnnualSubscription = entitlement.billingFrequency === BillingFrequency.ANNUAL;
  const { openModalDialog } = useModalDialog();
  const { openSidebar } = useSidebar();
  const handleSidebarAction = (openSidebarPayload: OpenSidebarPayload) => {
    openSidebar(openSidebarPayload);
  };
  const transactionAccountContext = React.useContext(TransactionAccountContext);
  const isActive = lowerCase(entitlement?.billingStatus ?? '') === lowerCase(BillingStatus.ACTIVE);
  const { data, getTransactionAccount } = transactionAccountContext;
  const txaccount = getTransactionAccount(data);
  const isSandbox = isEnabled(ENABLE_SANDBOX_ENTITLEMENT) && entitlement?.isSandbox;
  const availableInvoiceGroups = defaultTo(txaccount?.availableInvoiceGroups?.nodes, []) as InvoiceGroup[];
  return (
    <>
      {isAnnualSubscription && (isJCAProduct(entitlement.productId) || entitlement?.isApp) && (
        <SummarySectionV2.SummaryGridCalloutItem testId="summary-user-tier">
          <div className="billing-cycle title-height">
            <label>User tier</label>
            <div className="entitlement-details-column">
              <CopyableText>
                <span>{getUserTier(entitlement.subscription)}</span>
              </CopyableText>
              {/* {isActive &&
                !isSandbox &&
                !isDeletedEntitlement(entitlement) &&
                !isBilledElsewhereProduct(entitlement?.productId) &&
                isAccess(Permissions.SUBSCRIPTION_WRITE) &&
                isResourceAccess(Permissions.ENTITLEMENT_ALL) && (
                  <Button
                    data-testId="change-user-tier"
                    aria-label="Change user tier"
                    appearance="link"
                    className="inner-text"
                    onClick={() => {
                      validateSubscriptionDetails(entitlement, availableInvoiceGroups, txaccount)
                        ? handleSidebarAction({
                            sidebarType: SIDEBAR.STATUSPAGE_CHANGE_PLAN,
                            payload: {
                              entitlement,
                              transactionAccountContext,
                            },
                            sidebarOptions: {
                              isOverlay: true,
                              sidebarWidth: '600px',
                            },
                          })
                        : openModalDialog();
                    }}
                  >
                    Change
                  </Button>
                )} */}
            </div>
          </div>
        </SummarySectionV2.SummaryGridCalloutItem>
      )}
    </>
  );
};

export const UsersSection = ({
  entitlement,
  linkedEntitlements,
  isHistoryButtonEnabled = true,
  isProductRedirectEnabled = true,
}: props) => {
  const { activeUsers, isApp } = entitlement;
  const history = useHistory();

  if (isApp && linkedEntitlements.length > 0) {
    const sortedParentEntitlements = linkedEntitlements.sort(
      (a, b) => (Number(b?.activeUsers) ?? 0) - (Number(a?.activeUsers) ?? 0)
    );

    const { productId, entitlementNumber, cloudSiteId, activeUsers, productName } = sortedParentEntitlements[0];
    return (
      <SummarySectionV2.SummaryGridCalloutItem testId="summary-active-users">
        <div className="billing-plan billing-cycle title-height">
          <label>Users</label>
          <div className="entitlement-details-column">
            {activeUsers ? (
              <>
                <div>{activeUsers}</div>
                {isHistoryButtonEnabled && (
                  <Button
                    aria-label="User history"
                    appearance="link"
                    className="inner-text"
                    testId="user-history-btn"
                    onClick={() => history.push({ hash: '#user-history' })}
                  >
                    History
                  </Button>
                )}

                {isProductRedirectEnabled ? (
                  <Link
                    to={getEntitlementRouteCCPV2(productId, entitlementNumber as string, cloudSiteId)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid="product-link"
                    className="avatar-container"
                  >
                    {!!productId && <EntitlementIcon productId={productId} isCloud={true} className="avatar medium" />}{' '}
                    <span data-testid={productId}>{productName ?? 'Unnamed product'}</span>
                  </Link>
                ) : (
                  <div data-testid="product-link" className="avatar-container">
                    {!!productId && <EntitlementIcon productId={productId} isCloud={true} className="avatar medium" />}{' '}
                    <span data-testid={productId}>{productName ?? 'Unnamed product'}</span>
                  </div>
                )}
              </>
            ) : (
              <span className="data-unavailable">0</span>
            )}
          </div>
        </div>
      </SummarySectionV2.SummaryGridCalloutItem>
    );
  }

  return (
    <SummarySectionV2.SummaryGridCalloutItem testId="summary-active-users">
      <div className="billing-plan billing-cycle title-height">
        <label>Users</label>
        <div className="entitlement-details-column">
          {activeUsers ? (
            <>
              <div>{activeUsers}</div>
              {isHistoryButtonEnabled && (
                <Button
                  aria-label="User history"
                  appearance="link"
                  className="inner-text"
                  testId="user-history-btn"
                  onClick={() => history.push({ hash: '#user-history' })}
                >
                  History
                </Button>
              )}
            </>
          ) : (
            <span className="data-unavailable">0</span>
          )}
        </div>
      </div>
    </SummarySectionV2.SummaryGridCalloutItem>
  );
};
